import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

interface InlineTextEditorProps {
  isVisible: boolean;
  position: { x: number; y: number; width: number; height: number } | null;
  text: string;
  fontSize?: number;
  zIndex?: number;
  onTextChange: (text: string) => void;
  onComplete: () => void;
  onCancel: () => void;
}

const InlineTextEditor: React.FC<InlineTextEditorProps> = ({
  isVisible,
  position,
  text,
  fontSize = 16,
  zIndex = 1001,
  onTextChange,
  onComplete,
  onCancel
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPos, setToolbarPos] = useState({ top: -45, left: 0 });
  const [hiliteColor, setHiliteColor] = useState('#ffff00');
  const [foreColor, setForeColor] = useState('#000000');
  const selectionRange = useRef<Range | null>(null);

  // DOMPurify 설정: 리치 텍스트 서식에 필요한 태그/속성만 허용
  const sanitizeConfig = {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'span', 'br', 'p', 'div'],
    ALLOWED_ATTR: ['style'],
    ALLOWED_STYLE_PROPS: ['color', 'background-color', 'font-weight', 'font-style', 'text-decoration'],
  };

  useEffect(() => {
    if (isVisible && contentRef.current) {
      const sanitizedText = DOMPurify.sanitize(text, sanitizeConfig);
      if (contentRef.current.innerHTML !== sanitizedText) {
        contentRef.current.innerHTML = sanitizedText;
      }
      contentRef.current.focus();
      // 커서를 끝으로 이동
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(contentRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      selectionRange.current = range;

      setShowToolbar(true);
      updateToolbarPosition();
    }
  }, [isVisible]);

  // Click outside handler
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onComplete();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isVisible, onComplete]);

  const updateToolbarPosition = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && contentRef.current) {
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();

      if (rect && containerRect) {
        // Calculate relative position within the container
        // Position below the selection (rect.bottom)
        const top = rect.bottom - containerRect.top + 5; // 5px gap
        const left = rect.left - containerRect.left;

        // Ensure toolbar doesn't go off the left/right edges if possible (basic clamping could be added)
        setToolbarPos({ top, left });
      }
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      selectionRange.current = sel.getRangeAt(0);
      updateToolbarPosition();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newText = e.currentTarget.innerHTML;
    onTextChange(newText);
    saveSelection();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // defer selection save to let cursor move
    setTimeout(saveSelection, 0);

    if (e.key === 'Enter' && e.ctrlKey) {
      // Ctrl+Enter: 입력 완료
      e.preventDefault();
      onComplete();
    } else if (e.key === 'Escape') {
      // Escape: 입력 취소
      e.preventDefault();
      onCancel();
    }
  };

  const handleMouseUp = () => {
    saveSelection();
  };

  const handleKeyUp = () => {
    saveSelection();
  }

  const execCommand = (command: string, value: string | undefined = undefined) => {
    // 저장된 선택 영역 복원
    if (selectionRange.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(selectionRange.current);
    }

    document.execCommand(command, false, value);
    if (contentRef.current) {
      contentRef.current.focus();
      onTextChange(contentRef.current.innerHTML);
      saveSelection(); // 명령 실행 후 선택 영역 다시 저장
    }
  };

  if (!isVisible || !position) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: position.width,
        height: 'auto', // 높이 자동 조절
        minHeight: position.height,
        zIndex: zIndex,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 툴바 */}
      {showToolbar && (
        <div
          style={{
            position: 'absolute',
            top: `${toolbarPos.top}px`,
            left: `${toolbarPos.left}px`,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            padding: '4px',
            display: 'flex',
            gap: '4px',
            zIndex: zIndex + 1,
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
          onMouseDown={(e) => {
            // Prevent default to avoid losing focus from contentEditable, 
            // but allow interaction with inputs
            if (e.target instanceof HTMLElement && (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL')) {
              return;
            }
            e.preventDefault();
          }}
        >
          <button
            onClick={() => execCommand('bold')}
            style={{ fontWeight: 'bold', padding: '4px 8px', cursor: 'pointer', border: '1px solid #eee', borderRadius: '3px', background: 'white', minWidth: '24px' }}
            title="Bold (Ctrl+B)"
          >
            B
          </button>
          <button
            onClick={() => execCommand('italic')}
            style={{ fontStyle: 'italic', padding: '4px 8px', cursor: 'pointer', border: '1px solid #eee', borderRadius: '3px', background: 'white', fontFamily: 'serif', minWidth: '24px' }}
            title="Italic (Ctrl+I)"
          >
            I
          </button>
          <button
            onClick={() => execCommand('underline')}
            style={{ textDecoration: 'underline', padding: '4px 8px', cursor: 'pointer', border: '1px solid #eee', borderRadius: '3px', background: 'white', minWidth: '24px' }}
            title="Underline (Ctrl+U)"
          >
            U
          </button>

          {/* 형광펜 색상 선택 (Split Button) */}
          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #eee', borderRadius: '3px', background: 'white', marginRight: '2px' }}>
            <button
              onClick={() => execCommand('hiliteColor', hiliteColor)}
              style={{
                cursor: 'pointer',
                padding: '0 4px',
                height: '24px',
                fontSize: '14px',
                backgroundColor: hiliteColor,
                border: 'none',
                borderRight: '1px solid #eee',
                borderTopLeftRadius: '3px',
                borderBottomLeftRadius: '3px',
                minWidth: '24px'
              }}
              title="Highlight Text"
            >
              H
            </button>
            <div style={{ position: 'relative', width: '16px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <label htmlFor="hilite-color-picker" style={{ cursor: 'pointer', fontSize: '10px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▼</label>
              <input
                id="hilite-color-picker"
                type="color"
                value={hiliteColor}
                onChange={(e) => {
                  setHiliteColor(e.target.value);
                  execCommand('hiliteColor', e.target.value);
                }}
                style={{ position: 'absolute', top: 0, left: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                title="Select Highlight Color"
              />
            </div>
          </div>

          {/* 글자 색상 선택 (Split Button) */}
          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #eee', borderRadius: '3px', background: 'white', marginRight: '2px' }}>
            <button
              onClick={() => execCommand('foreColor', foreColor)}
              style={{
                cursor: 'pointer',
                padding: '0 4px',
                height: '24px',
                fontSize: '14px',
                color: foreColor,
                fontWeight: 'bold',
                backgroundColor: 'white',
                border: 'none',
                borderRight: '1px solid #eee',
                borderTopLeftRadius: '3px',
                borderBottomLeftRadius: '3px',
                minWidth: '24px'
              }}
              title="Text Color"
            >
              A
            </button>
            <div style={{ position: 'relative', width: '16px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <label htmlFor="fore-color-picker" style={{ cursor: 'pointer', fontSize: '10px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▼</label>
              <input
                id="fore-color-picker"
                type="color"
                value={foreColor}
                onChange={(e) => {
                  setForeColor(e.target.value);
                  execCommand('foreColor', e.target.value);
                }}
                style={{ position: 'absolute', top: 0, left: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                title="Select Text Color"
              />
            </div>
          </div>

          <button
            onClick={() => execCommand('removeFormat')}
            style={{ padding: '4px 8px', cursor: 'pointer', border: '1px solid #eee', borderRadius: '3px', background: 'white', minWidth: '24px' }}
            title="Clear Formatting"
          >
            ❌
          </button>
        </div>
      )}

      <div
        ref={contentRef}
        className="inline-text-edit"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        style={{
          width: '100%',
          height: '100%',
          minHeight: position.height,
          border: 'none',
          padding: '0',
          margin: '0',
          fontSize: `${fontSize}px`,
          fontFamily: 'Arial, sans-serif',
          lineHeight: '1.2',
          outline: 'none',
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          color: 'black',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          textAlign: 'left',
          verticalAlign: 'top',
        }}
      />
    </div>
  );
};

export default InlineTextEditor;
